/** Single source of truth for the challenge iframe, shared with the theme's
 * global `:has()` rule so the CSS selector and JS lookups can't drift. */
export const RECAPTCHA_CHALLENGE_IFRAME_SELECTOR =
  'iframe[src*="recaptcha/api2/bframe"]';

const getRecaptchaChallengeOverlay = (): HTMLElement | null => {
  if (typeof document === "undefined") return null;

  const challengeIframe = document.querySelector(
    RECAPTCHA_CHALLENGE_IFRAME_SELECTOR
  );
  return (challengeIframe?.closest("body > div") as HTMLElement) ?? null;
};

/**
 * reCAPTCHA appends its challenge overlay to `<body>`, outside the dialog
 * portal. Without this, a modal dialog treats every click on the challenge as
 * an outside interaction and dismisses itself.
 */
export const recaptchaPersistentElements = [getRecaptchaChallengeOverlay];

const enableOverlayPointerEvents = (): void => {
  const overlay = getRecaptchaChallengeOverlay();
  if (overlay && overlay.style.pointerEvents !== "auto") {
    overlay.style.pointerEvents = "auto";
  }
};

/**
 * Imperative fallback for the theme's `body > div:has(...)` rule: browsers
 * without CSS `:has()` (Safari < 15.4, Firefox < 121) can't re-enable pointer
 * events on the overlay that way, so we set it directly and keep it set while
 * reCAPTCHA mutates its own DOM. Returns a cleanup that stops observing.
 */
export const observeRecaptchaOverlayPointerEvents = (): (() => void) => {
  if (
    typeof document === "undefined" ||
    typeof MutationObserver === "undefined"
  ) {
    return () => {};
  }

  enableOverlayPointerEvents();

  const observer = new MutationObserver(() => enableOverlayPointerEvents());
  observer.observe(document.body, { childList: true, subtree: true });

  return () => observer.disconnect();
};
